package com.yendo.mx.models

import kotlinx.serialization.Serializable
import kotlinx.serialization.SerialName
import kotlinx.serialization.KSerializer
import kotlinx.serialization.ExperimentalSerializationApi
import kotlinx.serialization.descriptors.PrimitiveKind
import kotlinx.serialization.descriptors.PrimitiveSerialDescriptor
import kotlinx.serialization.descriptors.SerialDescriptor
import kotlinx.serialization.encoding.Decoder
import kotlinx.serialization.encoding.Encoder
import dev.gitlive.firebase.firestore.Timestamp
import com.yendo.mx.utils.getAhora

// 🚀 SERIALIZADOR MÁGICO: Maneja Long y Timestamp sin crashear
object FlexibleLongSerializer : KSerializer<Long> {
    override val descriptor: SerialDescriptor = PrimitiveSerialDescriptor("FlexibleLong", PrimitiveKind.LONG)
    override fun serialize(encoder: Encoder, value: Long) = encoder.encodeLong(value)

    override fun deserialize(decoder: Decoder): Long {
        return try {
            // Intenta leer como Long (milisegundos)
            decoder.decodeLong()
        } catch (_: Exception) {
            try {
                // Si falla, intenta leer como objeto Timestamp de Firebase
                val ts = decoder.decodeSerializableValue(Timestamp.serializer())
                (ts.seconds * 1000) + (ts.nanoseconds / 1000000)
            } catch (_: Exception) {
                0L // Valor por defecto si todo falla
            }
        }
    }
}

// 🚀 SERIALIZADOR MÁGICO PARA NULOS
object FlexibleLongNullableSerializer : KSerializer<Long?> {
    override val descriptor: SerialDescriptor = PrimitiveSerialDescriptor("FlexibleLongNullable", PrimitiveKind.LONG)
    
    @OptIn(ExperimentalSerializationApi::class)
    override fun serialize(encoder: Encoder, value: Long?) {
        if (value == null) encoder.encodeNull() else encoder.encodeLong(value)
    }

    @OptIn(ExperimentalSerializationApi::class)
    override fun deserialize(decoder: Decoder): Long? {
        return try {
            if (!decoder.decodeNotNullMark()) return decoder.decodeNull()
            decoder.decodeLong()
        } catch (_: Exception) {
            try {
                val ts = decoder.decodeSerializableValue(Timestamp.serializer())
                (ts.seconds * 1000) + (ts.nanoseconds / 1000000)
            } catch (_: Exception) {
                null
            }
        }
    }
}

@Serializable
data class PedidoFirebase(
    val idPedido: String = "",
    val idCorto: String = "", // 🚀 NUEVO: Folio de 6 letras indexable para búsquedas ultrarrápidas
    val idUsuario: String = "",
    val idZona: String = "", // 🚀 NUEVO: ID de la zona para el backend

    @SerialName("id_negocio")
    val idNegocio: String = "",

    @SerialName("nombreNegocio")
    val nombreNegocio: String = "Negocio Yendo",

    @SerialName("logoNegocio")
    val logoNegocio: String = "",

    val idRepartidor: String? = null,

    val estadoActual: String = "PENDIENTE",
    val url_pago: String? = null,
    val datos_bancarios: Map<String, String>? = null,
    val tipoPedido: String = "DELIVERY",
    val esProgramado: Boolean = false,
    @Serializable(with = FlexibleLongNullableSerializer::class) val fechaProgramada: Long? = null,

    // 🚀 CORRECCIÓN: Firebase usa int64, así que en Kotlin DEBE ser Long
    val estrellasRepartidor: Long? = null,
    val comentarioRepartidor: String? = null,
    val estrellasRestaurante: Long? = null,
    val comentarioRestaurante: String? = null,

    val metodoPago: String = "Efectivo",
    val conCuantoPaga: Double? = null,
    val cambioAEntregar: Double? = null,

    // 🚀 NUEVOS CAMPOS PARA COBRO AUTOMÁTICO / PRE-AUTORIZACIÓN
    val idTarjetaSeleccionada: String? = null,
    val tokenPagar: String? = null, // 👈 NUEVO: El token temporal para el cobro inmediato (un solo uso)
    val requierePreAutorizacion: Boolean = false,
    val cobroAutomaticoFallido: Boolean = false, // 👈 NUEVO: El backend lo cambia a true si el banco rechaza

    // 🚀 NUEVOS CAMPOS PARA COMERCIOS
    val accionAgotado: String? = null,
    val pagoPostArmado: Boolean = false,
    val tiempo_estimado_cocina: Double = 20.0, // 🚀 MINUTOS PARA COCINAR
    val aceptadoPorComercio: Boolean? = null, // 🚀 NUEVO: Confirmación de pedido programado
    val alerta7MinEnviada: Boolean = false, // 🚀 BANDERA PARA DISPATCHING

    val productos: List<CartItemFirebase> = emptyList(),
    @SerialName("productos_solicitados") val productosSolicitados: List<CartItemFirebase> = emptyList(), // 🚀 BACKUP INMUTABLE
    val ubicacionEntrega: UbicacionPedido = UbicacionPedido(),
    val ubicacionNegocio: UbicacionPedido = UbicacionPedido(),
    val tiempos: TiemposPedido = TiemposPedido(),

    val desgloseCliente: DesgloseCliente = DesgloseCliente(),
    val contratoFinanciero: ContratoFinanciero? = null,
    // Dentro de tu data class PedidoFirebase, agrega esto:
    val idCuponUsado: String? = null,
    val descuento: Double = 0.0,
    val patrocinadorDescuento: String? = null,

    val totalPagadoFinal: Double? = null, // 🚀 FALLBACK RAÍZ
    @SerialName("total_pagado_final") val totalPagadoFinalSnake: Double? = null, // 🚀 FALLBACK RAÍZ SNAKE

    val motivoCancelacion: String? = null,
    @SerialName("motivo_cancelacion") val motivoCancelacionSnake: String? = null,

    val requiresPin: Boolean = false, // Solo para compatibilidad
    
    // 🚀 NUEVOS CAMPOS PARA MP SCORE Y FRAUDE
    val nombreCliente: String = "",
    val apellidoCliente: String = "",
    val emailCliente: String = "",
    val telefonoCliente: String = "", // 🚀 NUEVO: Telefono del cliente para el comercio
    val issuerId: String? = null,

    // 🚀 NUEVOS CAMPOS PARA MANDADITOS (P2P)
    val datosMandadito: DatosMandadito? = null,

    @SerialName("datos_repartidor")
    val datosRepartidor: Map<String, String>? = null
) {
    val idRestaurante: String get() = idNegocio
    val nombreRestaurante: String get() = nombreNegocio
    val logoRestaurante: String get() = logoNegocio
}

@Serializable
data class DatosMandadito(
    val rolUsuario: String = "ENVIANDO", // 🚀 "ENVIANDO" o "RECIBIENDO"
    val tipoPaquete: String = "",
    val vehiculoSugerido: String = "",
    val nombreReceptor: String = "",
    val telefonoReceptor: String = "",
    val nombreRemitente: String = "",
    val telefonoRemitente: String = "",
    val requierePin: Boolean = false,
    val pinSeguridad: String? = null,
    val distanciaKm: Double = 0.0,
    val quienPaga: String = "REMITENTE",
    val fotoRecoleccionUrl: String? = null,
    val fotoEntregaUrl: String? = null,
    
    // 🚀 NUEVOS CAMPOS PARA DESCRIPCIÓN DE PAQUETE
    val descripcionPaquete: String = "",
    val fotoPaqueteUrl: String? = null
)

@Serializable
data class AdicionalFirebase(
    val nombre_grupo: String = "", // 🚀 INYECTADO: Fundamental para el Ticket del Comercio
    val nombre: String = "",
    val precioExtra: Double = 0.0
)

@Serializable
data class CartItemFirebase(
    val id: String = "",
    val nombre: String = "",
    // 🚀 CORRECCIÓN: Cambiado a Double para soportar pesos fraccionados (ej. 1.5kg)
    val cantidad: Double = 0.0,
    val precioUnitario: Double = 0.0,
    val notas: String = "",
    // 🚀 REEMPLAZO LIMPIO: Ahora es una lista de objetos
    val adicionales: List<AdicionalFirebase> = emptyList(),

    // 🚀 INYECCIÓN DE DATOS DE LOGÍSTICA DE PICKING
    @SerialName("tipo_venta") val tipoVenta: String = "UNIDAD",
    @SerialName("peso_estimado_kg") val pesoEstimadoKg: Double = 0.0,
    @SerialName("modo_compra") val modoCompra: String = "UNIDAD",
    @SerialName("precio_por") val precioPor: String = "PIEZA",
    @SerialName("peso_real_kg") val pesoRealKg: Double? = null,
    
    // 🚀 NUEVOS CAMPOS DE PICKING REACTIVO
    @SerialName("agregado_en_picking") val agregadoEnPicking: Boolean = false,
    @SerialName("sustituto_de") val sustitutoDe: String? = null
)

@Serializable
data class UbicacionPedido(
    val direccion: String = "",
    val latitude: Double = 0.0,
    val longitude: Double = 0.0,
    val tipoUbicacion: String = "Casa",
    val opcionEntrega: String = "Entregar en puerta",
    val indicaciones: String = ""
)

@Serializable
data class TiemposPedido(
    @Serializable(with = FlexibleLongSerializer::class) val creado: Long = getAhora(),
    @Serializable(with = FlexibleLongNullableSerializer::class) val aceptado: Long? = null,
    @Serializable(with = FlexibleLongNullableSerializer::class) val preparando: Long? = null,
    @Serializable(with = FlexibleLongNullableSerializer::class) val buscandoRepartidor: Long? = null,
    @Serializable(with = FlexibleLongNullableSerializer::class) val recolectado: Long? = null,
    @Serializable(with = FlexibleLongNullableSerializer::class) val enCamino: Long? = null,
    @SerialName("en_camino") @Serializable(with = FlexibleLongNullableSerializer::class) val enCaminoSnake: Long? = null,
    @Serializable(with = FlexibleLongNullableSerializer::class) val enPuerta: Long? = null,
    @SerialName("en_puerta") @Serializable(with = FlexibleLongNullableSerializer::class) val enPuertaSnake: Long? = null,
    @Serializable(with = FlexibleLongNullableSerializer::class) val concluido: Long? = null,
    @SerialName("entregado") @Serializable(with = FlexibleLongNullableSerializer::class) val entregadoSnake: Long? = null
) {
    val enCaminoFinal: Long? get() = enCamino ?: enCaminoSnake
    val enPuertaFinal: Long? get() = enPuerta ?: enPuertaSnake
    val concluidoFinal: Long? get() = concluido ?: entregadoSnake
}

@Serializable
data class DesgloseCliente(
    val subtotalProductos: Double = 0.0,
    val subtotalProductosOriginal: Double? = null,
    val costoEnvio: Double = 0.0,
    val propina: Double = 0.0,
    val tarifaServicio: Double = 0.0,
    val descuentoCupon: Double = 0.0,
    val totalPagado: Double = 0.0,
    val totalPagadoFinal: Double? = null,
    @SerialName("total_pagado_final") val totalPagadoFinalSnake: Double? = null
) {
    val subtotalProductosActual: Double get() = subtotalProductos
    val subtotalOriginalActual: Double? get() = subtotalProductosOriginal
}

@Serializable
data class ContratoFinanciero(
    val reglaCaja: String = "TOTAL",
    val sistemaBalance: String = "FONDEO_REPARTIDOR",
    val recaudadorComision: String = "NEGOCIO",
    val patrocinadorCupon: String = "NINGUNO",
    val montoPagoANegocio: Double = 0.0,
    val montoCobroACliente: Double = 0.0,
    val balanceFinal: BalanceFinal = BalanceFinal()
)

@Serializable
data class BalanceFinal(
    @SerialName("netoNegocio")
    val netoNegocio: Double = 0.0,
    val netoRepartidor: Double = 0.0,
    val netoYendoMx: Double = 0.0
)
